import { QueryClient } from '@tanstack/react-query';
import { CubeQuery, CubeQueryRecord, RecordQueryDirectData, RecordingSetupInfo } from '../types/cubeQueryTypes';
import { CubejsApi } from './RecordingState';
import { QueryStore } from './QueryStore';
import { TabStateManager } from './TabStateManager';
import { CacheSubscriber } from './CacheSubscriber';
import { extractComponentNameWithFallback } from './componentNameExtractor';
import { clearRequestTimings } from './queryInterceptor';

export class QueryRecorder {
  private cubejsApi: CubejsApi | null = null;

  private queryClient: QueryClient | null = null;

  private cacheSubscriber: CacheSubscriber;

  constructor(
    private queryStore: QueryStore,
    private tabStateManager: TabStateManager,
  ) {
    this.cacheSubscriber = new CacheSubscriber(
      queryStore,
      tabStateManager,
      () => this.cubejsApi?.apiUrl || '',
      () => false,
      () => this.generateRequestId(),
    );
  }

  setCubejsApi(api: CubejsApi): void {
    this.cubejsApi = api;
  }

  setQueryClient(client: QueryClient): void {
    this.queryClient = client;
    this.cacheSubscriber.setQueryClient(client);
  }

  extractQueryKeyFromReactQuery(cubeQuery: CubeQuery): string[] | undefined {
    if (!this.queryClient) return undefined;

    try {
      const cache = this.queryClient.getQueryCache();
      const allQueries = cache.getAll();
      const queryStr = JSON.stringify(cubeQuery);

      const match = allQueries.find(q =>
        q.queryKey.some(
          (part: unknown) => typeof part === 'object' && part !== null && JSON.stringify(part) === queryStr,
        ),
      );

      if (match) {
        return match.queryKey.filter((key): key is string => typeof key === 'string');
      }
    } catch {
      return undefined;
    }

    return undefined;
  }

  startRecording(): void {
    if (this.queryStore.getRecording()) {
      return;
    }

    this.queryStore.setRecording(true);
    this.queryStore.clearQueries();
    clearRequestTimings();
    this.cacheSubscriber.clearCapturedHashes();
    this.cacheSubscriber.startListening();

    this.queryStore.persistState();
    this.queryStore.notifyListeners();
  }

  stopRecording(): void {
    this.queryStore.setRecording(false);
    this.cacheSubscriber.stopListening();
    this.queryStore.persistState();
    this.queryStore.notifyListeners();
  }

  updatePagePath(pagePath: string): void {
    this.cacheSubscriber.updatePagePath(pagePath);
  }

  generateRequestId(): string {
    return `cube-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  recordQueryDirect(data: RecordQueryDirectData): void {
    if (!this.queryStore.getRecording()) return;

    const pagePath = data.pagePath || (typeof window !== 'undefined' ? window.location.pathname : '/');

    const record: CubeQueryRecord = {
      id: this.generateRequestId(),
      timestamp: data.timestamp,
      componentName: data.componentName,
      pagePath,
      tabName: this.tabStateManager.getActiveTab() || undefined,
      query: data.query,
      queryKey: [],
      url: this.cubejsApi?.apiUrl || 'cube-api',
      method: 'CUBE_LOAD',
      duration: Math.round(data.duration),
      status: 200,
    };

    if (!this.queryStore.isFull()) {
      this.queryStore.addQuery(record);
    }
  }

  async refreshQuery(_queryId: string): Promise<void> {
    return undefined;
  }

  recordQuery(query: CubeQuery): void {
    if (!this.queryStore.getRecording() || !query) return;

    const timestamp = Date.now();
    const componentName = extractComponentNameWithFallback(query);
    const currentTab = this.tabStateManager.getActiveTab();

    const queryRecord: CubeQueryRecord = {
      id: `${timestamp}-${Math.random()}`,
      query,
      componentName,
      timestamp,
      pagePath: typeof window !== 'undefined' ? window.location.pathname : '/',
      tabName: currentTab,
      queryKey: [],
      url: this.cubejsApi?.apiUrl || 'cube-api',
      method: 'CUBE_LOAD',
      status: 200,
    };

    if (!this.queryStore.isFull()) {
      this.queryStore.addQuery(queryRecord);
    }
  }

  getRecordingSetupInfo(): RecordingSetupInfo {
    return {
      isRecording: this.queryStore.getRecording(),
      queriesCount: this.queryStore.getCurrentCount(),
      maxQueries: this.queryStore.getMaxQueries(),
      hasCubejsApi: !!this.cubejsApi,
      hasQueryClient: !!this.queryClient,
      hasOriginalLoad: false,
      cubejsApiUrl: this.cubejsApi?.apiUrl,
    };
  }
}
